"use client";

import { useWallet } from "@aptos-labs/wallet-adapter-react";
import { Button } from "@decibeltrade/web-core/components";
import { ModalContent, openModal } from "@decibeltrade/web-core/modal";
import { useTranslations } from "next-intl";
import { QRCodeSVG } from "qrcode.react";
import { useCallback, useState } from "react";
import { css } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { connectWcEvm } from "@/contexts/walletconnect-evm-provider";
import { connectWcSolana } from "@/contexts/walletconnect-solana-provider";
import { env } from "@/env";
import { Logger } from "@/utils/logger";

import { ModalCloseButton } from "../modal/modal-close-button";
import { SupportedNetworkId } from "../modal/wallet-selector-modal";

const WC_EVM_DERIVED_WALLET_NAME = "WalletConnect (Ethereum)";
const WC_SOLANA_DERIVED_WALLET_NAME = "WalletConnect (Solana)";

const WALLETCONNECT_ICON_URL =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 480 480'%3E%3Crect width='480' height='480' rx='240' fill='%233396FF'/%3E%3Cpath d='M141.1 185.2c54.6-53.5 143.2-53.5 197.8 0l6.6 6.4a6.7 6.7 0 0 1 0 9.7l-22.5 22a3.5 3.5 0 0 1-4.9 0l-9-8.9c-38.1-37.3-99.9-37.3-138 0l-9.7 9.5a3.5 3.5 0 0 1-4.9 0l-22.5-22a6.7 6.7 0 0 1 0-9.7l7.1-7Zm244.5 45.5 20 19.7a6.7 6.7 0 0 1 0 9.6l-90.3 88.4a7 7 0 0 1-9.9 0l-64.1-62.8a1.8 1.8 0 0 0-2.5 0l-64.1 62.8a7 7 0 0 1-9.9 0l-90.3-88.4a6.7 6.7 0 0 1 0-9.6l20-19.7a7 7 0 0 1 9.9 0l64.1 62.8a1.8 1.8 0 0 0 2.5 0l64.1-62.8a7 7 0 0 1 9.9 0l64.1 62.8a1.8 1.8 0 0 0 2.5 0l64.1-62.8a7 7 0 0 1 9.9 0Z' fill='%23fff'/%3E%3C/svg%3E";

interface WalletConnectRowProps {
  onConnect?: (tab: SupportedNetworkId) => void;
}

/** Wallet selector row that triggers a WalletConnect session for EVM wallets. */
export function WalletConnectEvmRow({ onConnect }: WalletConnectRowProps) {
  const t = useTranslations("walletSelector");
  const [isConnecting, setIsConnecting] = useState(false);
  const projectId = env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID;
  const { connect } = useWallet();

  const handleConnect = useCallback(async () => {
    if (!projectId || isConnecting) return;
    setIsConnecting(true);

    let closeQrModal: (() => void) | undefined;

    try {
      await connectWcEvm(projectId, (uri) => {
        closeQrModal = openWcQrModal(uri);
      });
      closeQrModal?.();
      connect(WC_EVM_DERIVED_WALLET_NAME);
      onConnect?.("ethereum");
    } catch (error) {
      closeQrModal?.();
      Logger.error(error, { message: "WalletConnect EVM connection failed" });
    } finally {
      setIsConnecting(false);
    }
  }, [projectId, isConnecting, connect, onConnect]);

  if (!projectId) return null;

  return (
    <div
      className={flex({
        align: "center",
        justify: "space-between",
        gap: "16",
        pl: "20",
        pr: "[6px]",
        py: "[6px]",
      })}
    >
      <div className={flex({ align: "center", gap: "16" })}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={WALLETCONNECT_ICON_URL}
          alt={t("walletConnectBrand")}
          className={css({ h: "24", w: "24", borderRadius: "full" })}
        />
        <span className={css({ textStyle: "body.md" })}>{t("walletConnectBrand")}</span>
      </div>
      <Button size="sm" variant="secondary" onClick={handleConnect} disabled={isConnecting}>
        {isConnecting ? "Connecting..." : "Connect"}
      </Button>
    </div>
  );
}

/** Modal displaying a WalletConnect QR code for the user to scan with their mobile wallet. */
function WcQrModal({ close, uri }: { close: () => void; uri: string }) {
  const t = useTranslations("walletSelector");
  return (
    <ModalContent
      className={css({
        position: "relative",
        w: "[360px]",
        bg: "background.canvas",
        p: "24",
      })}
    >
      <ModalCloseButton close={close} />
      <div className={stack({ gap: "20", align: "center" })}>
        <div className={stack({ gap: "8", align: "center" })}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={WALLETCONNECT_ICON_URL}
            alt={t("walletConnectBrand")}
            className={css({ h: "40", w: "40", borderRadius: "full" })}
          />
          <h3 className={css({ textStyle: "highlight.lg" })}>{t("walletConnectBrand")}</h3>
          <p
            className={css({ textStyle: "body.md", color: "text.secondary", textAlign: "center" })}
          >
            {t("walletConnectQrInstructions")}
          </p>
        </div>
        <div className={css({ p: "16", bg: "white", borderRadius: "200" })}>
          <QRCodeSVG value={uri} size={240} />
        </div>
      </div>
    </ModalContent>
  );
}

/** Wallet selector row that triggers a WalletConnect session for Solana wallets. */
export function WalletConnectSolanaRow({ onConnect }: WalletConnectRowProps) {
  const t = useTranslations("walletSelector");
  const [isConnecting, setIsConnecting] = useState(false);
  const projectId = env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID;
  const { connect } = useWallet();

  const handleConnect = useCallback(async () => {
    if (!projectId || isConnecting) return;
    setIsConnecting(true);

    let closeQrModal: (() => void) | undefined;

    try {
      await connectWcSolana(projectId, (uri) => {
        closeQrModal = openWcQrModal(uri);
      });
      closeQrModal?.();
      connect(WC_SOLANA_DERIVED_WALLET_NAME);
      onConnect?.("solana");
    } catch (error) {
      closeQrModal?.();
      Logger.error(error, { message: "WalletConnect Solana connection failed" });
    } finally {
      setIsConnecting(false);
    }
  }, [projectId, isConnecting, connect, onConnect]);

  if (!projectId) return null;

  return (
    <div
      className={flex({
        align: "center",
        justify: "space-between",
        gap: "16",
        pl: "20",
        pr: "[6px]",
        py: "[6px]",
      })}
    >
      <div className={flex({ align: "center", gap: "16" })}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={WALLETCONNECT_ICON_URL}
          alt={t("walletConnectBrand")}
          className={css({ h: "24", w: "24", borderRadius: "full" })}
        />
        <span className={css({ textStyle: "body.md" })}>{t("walletConnectBrand")}</span>
      </div>
      <Button size="sm" variant="secondary" onClick={handleConnect} disabled={isConnecting}>
        {isConnecting ? "Connecting..." : "Connect"}
      </Button>
    </div>
  );
}

/** Open the WalletConnect QR modal and return a function to close it programmatically. */
function openWcQrModal(uri: string): () => void {
  let closeRef: (() => void) | undefined;

  openModal({
    id: "walletconnect-qr",
    renderContent: ({ close }) => {
      closeRef = close;
      return <WcQrModal close={close} uri={uri} />;
    },
  });

  return () => closeRef?.();
}
